import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type React from 'react';

import {
  buildGroupsWithRegimes,
  getGroupSortableId,
  type SidebarDragData,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-drag';
import type {
  GroupWithRegimes,
  RegimeGroup,
  RegimeItem,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-types';

interface UseRegimesSidebarLocalStateParams {
  groups: RegimeGroup[];
  regimes: RegimeItem[];
  activeDragData: SidebarDragData | null;
}

interface UseRegimesSidebarLocalStateResult {
  localGroups: RegimeGroup[];
  setLocalGroups: React.Dispatch<React.SetStateAction<RegimeGroup[]>>;
  localRegimes: RegimeItem[];
  setLocalRegimes: React.Dispatch<React.SetStateAction<RegimeItem[]>>;
  groupsWithRegimes: GroupWithRegimes[];
  groupIds: string[];
  hasPendingGroupUpdate: boolean;
  pendingGroupUpdateRef: React.MutableRefObject<boolean>;
  pendingGroupIds: Set<string | number>;
  pendingRegimeIds: Set<string | number>;
  isGroupPending: (groupId: string | number) => boolean;
  isRegimePending: (regimeId: string | number) => boolean;
  addPendingGroup: (groupId: string | number) => void;
  removePendingGroup: (groupId: string | number) => void;
  addPendingRegime: (regimeId: string | number) => void;
  removePendingRegime: (regimeId: string | number) => void;
}

export const useRegimesSidebarLocalState = ({
  groups,
  regimes,
  activeDragData,
}: UseRegimesSidebarLocalStateParams): UseRegimesSidebarLocalStateResult => {
  const [localGroups, setLocalGroups] = useState<RegimeGroup[]>(groups);
  const [localRegimes, setLocalRegimes] = useState<RegimeItem[]>(regimes);
  const [pendingGroupIds, setPendingGroupIds] = useState<Set<string | number>>(
    new Set()
  );
  const [pendingRegimeIds, setPendingRegimeIds] = useState<
    Set<string | number>
  >(new Set());
  const previousGroupsRef = useRef(groups);
  const previousRegimesRef = useRef(regimes);
  const pendingGroupUpdateRef = useRef(false);

  useEffect(() => {
    if (previousGroupsRef.current === groups) return;
    if (pendingGroupIds.size) return;

    previousGroupsRef.current = groups;
    setLocalGroups(groups);
  }, [groups, pendingGroupIds.size]);

  useEffect(() => {
    if (previousRegimesRef.current === regimes) return;
    if (activeDragData || pendingRegimeIds.size) return;

    previousRegimesRef.current = regimes;
    setLocalRegimes(regimes);
  }, [activeDragData, pendingRegimeIds.size, regimes]);

  const wasDraggingRef = useRef(false);
  const preDragGroupIdsRef = useRef<Set<string | number> | null>(null);

  if (activeDragData && !wasDraggingRef.current) {
    wasDraggingRef.current = true;
    preDragGroupIdsRef.current = new Set(localRegimes.map((r) => r.groupId));
  } else if (!activeDragData && wasDraggingRef.current) {
    wasDraggingRef.current = false;
    preDragGroupIdsRef.current = null;
  }

  const groupsWithRegimes = useMemo(() => {
    const all = buildGroupsWithRegimes(localGroups, localRegimes, true);
    const preserveIds = preDragGroupIdsRef.current;

    if (!preserveIds) {
      return all.filter((g) => !!g.regimes.length);
    }

    return all.filter((g) => !!g.regimes.length || preserveIds.has(g.id));
  }, [localGroups, localRegimes, activeDragData]);

  const groupIds = useMemo(
    () => groupsWithRegimes.map((g) => getGroupSortableId(g.id)),
    [groupsWithRegimes]
  );
  const hasPendingGroupUpdate = !!pendingGroupIds.size;

  const isGroupPending = useCallback(
    (groupId: string | number) => pendingGroupIds.has(groupId),
    [pendingGroupIds]
  );

  const isRegimePending = useCallback(
    (regimeId: string | number) => pendingRegimeIds.has(regimeId),
    [pendingRegimeIds]
  );

  const addPendingGroup = useCallback((groupId: string | number) => {
    setPendingGroupIds((prev) => {
      const next = new Set(prev);

      next.add(groupId);

      return next;
    });
  }, []);

  const removePendingGroup = useCallback((groupId: string | number) => {
    setPendingGroupIds((prev) => {
      const next = new Set(prev);

      next.delete(groupId);

      return next;
    });
  }, []);

  const addPendingRegime = useCallback((regimeId: string | number) => {
    setPendingRegimeIds((prev) => {
      const next = new Set(prev);

      next.add(regimeId);

      return next;
    });
  }, []);

  const removePendingRegime = useCallback((regimeId: string | number) => {
    setPendingRegimeIds((prev) => {
      const next = new Set(prev);

      next.delete(regimeId);

      return next;
    });
  }, []);

  return {
    localGroups,
    setLocalGroups,
    localRegimes,
    setLocalRegimes,
    groupsWithRegimes,
    groupIds,
    hasPendingGroupUpdate,
    pendingGroupUpdateRef,
    pendingGroupIds,
    pendingRegimeIds,
    isGroupPending,
    isRegimePending,
    addPendingGroup,
    removePendingGroup,
    addPendingRegime,
    removePendingRegime,
  };
};
